/**
 * Implementation of Onboarding Layout component
 * 
 * @author - Harsh Tomar
 */

// Dependencies
import React, { useCallback, useState, ChangeEvent, useEffect } from "react";
import { FiShield } from "react-icons/fi";
import { RiSpaceShipFill } from "react-icons/ri";
import { When } from "react-if";
import { useMutation } from "react-query";
import { Outlet, useNavigate } from "react-router-dom";
import { createMonitor } from "../../../api/reactQuery";
import { httpsUrlValidator, removeScheme } from "../../../utils";


export default function MonitorMain() {

  const [urlRaw, setUrl] = useState('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');

  const [finalUrl, setFinalUrl] = useState('');
  const [step, setStep] = useState(0);

  const navigate = useNavigate();

  const { mutate } = useMutation((newMutation: { type: string, name: string, url: string }) => createMonitor(newMutation), {
    onError: () => {
      // navigate to home page
      navigate("/dashboard/home", {
        state: {
          from: 'onboarding/monitor-main',
          status: 'FAILED'
        }
      })
    },
    onSuccess: (response, variable) => {
      // navigate to home page
      setStep(4);
      setTimeout(() => {
        navigate("/dashboard/home", {
          state: {
            from: 'onboarding/monitor-main',
            status: 'SUCCESS'
          }
        })
      }, 2000)
    }
  })

  useEffect(() => {

  }, [urlRaw]);

  const onChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
    setError('');
    setTouched(true);
    setFinalUrl('');
    setStep(0)
  }, []);

  const onClickContinue = useCallback(() => {
    if (!urlRaw) {
      setError("Please enter a valid domain")
      return;
    }

    if (!httpsUrlValidator(urlRaw)) {
      setError("Invalid domain format")
      return;
    }

    setFinalUrl(urlRaw);

    setStep(3)
    mutate({
      type: "SSL_MONITORING",
      url: removeScheme(urlRaw),
      name: "New Domain"
    })
  }, [urlRaw])

  return (
    <div className="flex flex-col items-center bg-primary px-4">
      <div className="p-4 w-[495px]">

        <When condition={step === 0}>
          <div className="flex items-center mt-8">
            <h1 className="text-white text-xl font-extrabold text-center">Lets add your first domain</h1>
          </div>
          <form onSubmit={(e) => {
            e.preventDefault();
            onClickContinue();
          }}>
            <div className="mt-4">
              <input className="rounded-lg shadow-xl p-4 text-2xl input-field-primary w-full my-2" placeholder="yourdomain.com" onChange={onChangeInput}></input>
              <When condition={error}>
                <div>
                  <h3 className="text-red-500 text-base font-bold">{error}</h3>
                </div>
              </When>
            </div>

            <div className="mt-4">
              <button className="btn-accent mt-1.5 w-full p-4" >Start Tracking</button>
            </div>
          </form>
        </When>
        <When condition={step === 3}>
          <div className="flex items-center mt-8 flex-col">
            <h1 className="text-white text-xl font-extrabold text-center">Adding and preparing dashboard...</h1>
          </div>
        </When>
        <When condition={step === 4}>
          <div className="items-center flex flex-col">
            <div className="flex items-center mt-8">
              <h1 className="text-accent text-xl font-extrabold text-center">You domain has been added successfully</h1>
            </div>
            <div className="flex items-center mt-4">
              <h1 className="text-white text-xl font-extrabold text-center">Navigating to dashboard...</h1>
            </div>
          </div>
        </When>
      </div>
    </div>
  )
}